import * as React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './Chart';
import { cn } from '@root/lib/utils';
import Tooltip from '../v2/ui-elements/Tooltip';

export interface PieChartData {
  name: string;
  value: number;
  color?: string;
  [key: string]: unknown;
}

export interface LegendItem {
  name: string;
  value: number;
  displayValue: React.ReactNode;
  color: string;
  label?: string;
}

export interface PieChartProps {
  data: PieChartData[];
  config: Record<string, string | { label?: string; color?: string }>;
  className?: string;
  size?: number;
  innerRadius?: number;
  outerRadius?: number;
  showTooltip?: boolean;
  middleText?: string;
  middleValue?: number;
  // Legend props
  showLegend?: boolean;
  legendTitle?: string;
  legendSubtitle?: string;
  legendClickable?: boolean;
  onLegendClick?: (item: LegendItem) => void;
  // Formats the value shown in the legend's value column (e.g. add a currency symbol).
  // Receives the raw data item so per-row context (like a currency code) is available.
  formatLegendValue?: (item: PieChartData) => React.ReactNode;
  // Layout props
  layout?: 'horizontal' | 'vertical';
  containerHeight?: string;
  containerPadding?: string;
}

const PieChartComponent = React.forwardRef<HTMLDivElement, PieChartProps>(
  (
    {
      data,
      config,
      className,
      size = 220,
      innerRadius = 70,
      outerRadius = 100,
      showTooltip = true,
      middleText,
      middleValue,
      showLegend = false,
      legendTitle = 'Legend',
      legendSubtitle = 'Values',
      legendClickable = false,
      onLegendClick,
      formatLegendValue,
      layout = 'horizontal',
      containerHeight = '406px',
      containerPadding = '15px',
      ...props
    },
    ref,
  ) => {
    const totalValue = data.reduce((sum, item) => sum + item.value, 0);

    const renderLabel = (args: any) => {
      // Only render label for the first slice to avoid duplication
      if (args.index !== 0) return null;
      if (!middleText && !middleValue) return null;

      return (
        <g>
          <text
            x={args.cx}
            y={args.cy - 8}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-2xl font-bold fill-foreground"
            style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {middleValue || totalValue}
          </text>
          <text
            x={args.cx}
            y={args.cy + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-sm fill-muted-foreground"
            style={{ fontSize: '14px' }}>
            {middleText}
          </text>
        </g>
      );
    };

    const handleLegendClick = (item: LegendItem) => {
      if (legendClickable && onLegendClick) {
        onLegendClick(item);
      }
    };

    const legendData: LegendItem[] = data.map(item => {
      const colorValue =
        typeof config[item.name] === 'string'
          ? config[item.name]
          : (config[item.name] as { color?: string })?.color ||
            item.color ||
            `hsl(${data.indexOf(item) * 40}, 70%, 50%)`;

      return {
        name: item.name,
        value: item.value,
        displayValue: formatLegendValue ? formatLegendValue(item) : item.value,
        color: colorValue as string,
        label: item.name?.charAt(0)?.toUpperCase() + item.name?.slice(1)?.toLowerCase(),
      };
    });

    if (totalValue === 0) {
      return (
        <div
          ref={ref}
          className={cn('flex items-center justify-center', className)}
          style={{
            width: '100%',
            height: containerHeight,
            padding: containerPadding,
          }}
          {...props}>
          <div className="text-center text-muted-foreground">
            <div className="text-sm">No Data</div>
          </div>
        </div>
      );
    }

    // Convert config to ChartConfig format
    const chartConfig = Object.keys(config).reduce(
      (acc, key) => {
        const value = config[key];
        acc[key] = typeof value === 'string' ? { color: value } : { color: value.color };
        return acc;
      },
      {} as Record<string, { color?: string }>,
    );

    const chartElement = (
      <ChartContainer config={chartConfig} className="w-full h-full" style={{ width: size, height: size }}>
        <RechartsPieChart width={size} height={size}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            label={middleText || middleValue ? renderLabel : false}
            labelLine={false}
            strokeWidth={0}>
            {data.map((entry, index) => {
              const colorValue =
                typeof config[entry.name] === 'string'
                  ? config[entry.name]
                  : (config[entry.name] as { color?: string })?.color || entry.color || `hsl(${index * 40}, 70%, 50%)`;

              return <Cell key={`cell-${index}`} fill={colorValue as string} />;
            })}
          </Pie>
          {showTooltip && <ChartTooltip content={<ChartTooltipContent />} />}
        </RechartsPieChart>
      </ChartContainer>
    );

    const legendElement = showLegend ? (
      <div className={`flex-1  ${layout === 'vertical' ? 'w-full' : 'min-w-0'}`}>
        <div className="flex justify-between items-center mb-4 gap-2">
          <h4
            className="text-sm font-medium text-foreground m-0 truncate"
            style={{ fontSize: '14px', fontWeight: '500' }}
            title={legendTitle}>
            {legendTitle}
          </h4>
          <h4
            className="text-sm font-medium text-foreground m-0 flex-shrink-0"
            style={{ fontSize: '14px', fontWeight: '500' }}>
            {legendSubtitle}
          </h4>
        </div>
        <div className="flex flex-col gap-3">
          {legendData.map((item, index) => (
            <div
              key={`legend-${index}`}
              className={cn(
                'flex items-center justify-between gap-2 w-full',
                legendClickable && 'cursor-pointer hover:opacity-80',
              )}
              onClick={() => handleLegendClick(item)}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <Tooltip title={item.label}>
                  <span className="text-sm text-foreground capitalize SingleLine" style={{ fontSize: '14px' }}>
                    {item.label}
                  </span>
                </Tooltip>
              </div>
              <span
                className="text-sm font-medium text-blue-600 flex-shrink-0"
                style={{ fontSize: '14px', fontWeight: '500' }}>
                {item.displayValue}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null;

    return (
      <div
        ref={ref}
        className={cn(
          'w-full',
          layout === 'horizontal' ? 'flex items-center gap-6' : 'flex flex-col items-center gap-4',
          className,
        )}
        style={{
          height: containerHeight,
          padding: containerPadding,
        }}
        {...props}>
        {layout === 'horizontal' ? (
          <>
            <div className="flex-shrink-0">{chartElement}</div>
            {legendElement}
          </>
        ) : (
          <>
            {chartElement}
            {legendElement}
          </>
        )}
      </div>
    );
  },
);

PieChartComponent.displayName = 'PieChart';

export { PieChartComponent as PieChart };
